import React, { useState, useEffect, useMemo, useRef, CSSProperties, ReactNode, useCallback } from 'react';
import classNames from 'classnames';

import { useTheme } from '@cobalt/react-theme-provider';
import Box from '@cobalt/react-box';
import Dropdown, { Content } from '@cobalt/react-dropdown';
import List, { Item } from '@cobalt/react-list';
import { Text } from '@cobalt/react-typography';
import useOutsideClick from '@cobalt/react-outside-click-hook';

import { treeToList } from '../treeUtil';

import CascaderSelector from './CascaderSelector';
import CascaderListor from './CascaderListor';

import { CascaderSelectOptionType } from '../types';

const cls = 'cascader-select';

const LISTOR_MAX_HEIGHT = 200;

export interface CascaderSelectProps {
  value?: string[];
  tempVals?: string[];
  defaultValue?: string[];
  options: CascaderSelectOptionType[];
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  changeOnSelect?: boolean;
  loading?: boolean;
  transparent?: boolean;
  borderless?: boolean;
  size?: 'small' | 'medium';
  variation?: 'error' | 'warning';
  contentStyle?: CSSProperties;
  className?: string;
  style?: CSSProperties;
  prefixCls?: string;
  children?: ReactNode;
  noResultsFoundText?: string;
  backText?: string;
  onChange?: (value: string[]) => void;
  onClear?: () => void;
  onSearch?: (value: string) => void;
  onBlur?: () => void;
  onSelect: (value: string, level: number, isLastLevel?: boolean) => void;
}

function CascaderSelect(props: CascaderSelectProps) {
  const {
    value,
    tempVals,
    defaultValue,
    options,
    disabled = false,
    changeOnSelect,
    loading = false,
    borderless = false,
    size,
    className,
    contentStyle,
    prefixCls = 'cobalt',
    noResultsFoundText,
    onClear,
    onBlur,
    onSelect,
  } = props;

  const componentCls = `${prefixCls}-${cls}`;
  const mergeClassNames = classNames(`${componentCls}`, className);

  const theme = useTheme();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string[]>([]);
  const [isLastLevel, setIsLastLevel] = useState(false);

  const optionMap = useMemo((): Map<string, string> => {
    return treeToList(options).reduce((prev, cur) => {
      prev.set(cur.value, cur.label);
      return prev;
    }, new Map());
  }, [options]);

  const outsideClickHandler = useCallback(() => {
    if (!isOpen) return;
    !isLastLevel && onSelect?.('', -1);
    setIsOpen(false);
    onBlur?.();
  }, [isLastLevel, isOpen, onBlur, onSelect]);
  const { onClickCapture } = useOutsideClick(outsideClickHandler);

  const genDirection = () => {
    const { clientHeight } = document.body;
    const bottom = dropdownRef.current?.getBoundingClientRect().bottom || 0;
    const offsetBottom = clientHeight - bottom;
    return offsetBottom > LISTOR_MAX_HEIGHT ? 'down' : 'up';
  };

  const handleToggleOpen = (_open?: boolean) => {
    if (disabled || loading || _open === isOpen) return;
    onClickCapture();

    const open = _open === undefined ? !isOpen : _open;
    setIsOpen(open);
    if (!open) onBlur?.();
  };

  const changeSelect = (value: string, level: number, isLastLevel?: boolean) => {
    onSelect?.(value, level, isLastLevel);
    if (isLastLevel) {
      setIsOpen(false);
      onBlur?.();
    }
    setIsLastLevel(!!isLastLevel);
  };

  useEffect(() => {
    setSelectedValue(value || defaultValue! || []);
  }, [defaultValue, value]);

  return (
    <Dropdown
      data-testid="cascaderSelect"
      forwardedRef={dropdownRef}
      open={isOpen}
      borderless={borderless}
      direction={genDirection()}
    >
      <CascaderSelector
        {...props}
        isOpen={isOpen}
        inputRef={inputRef}
        selectedValue={selectedValue}
        optionMap={optionMap}
        size={size}
        className={mergeClassNames}
        onToggleOpen={handleToggleOpen}
        onClearSelectedValue={() => {
          onClear?.();
        }}
      />

      <Content style={contentStyle} data-testid="content" inputRef={inputRef.current} onClick={onClickCapture}>
        <Box>
          {/* Listor */}
          {!!options.length && (
            <CascaderListor
              selectedValue={isLastLevel || changeOnSelect ? selectedValue : tempVals!}
              options={options}
              style={{ maxHeight: LISTOR_MAX_HEIGHT }}
              onChangeSelect={changeSelect}
            />
          )}

          {/* Result message */}
          {!options.length && (
            <Box padding={2}>
              <List>
                <Item>
                  <Box paddingY={2} paddingX={4}>
                    <Text color={theme.gray500}>{noResultsFoundText}</Text>
                  </Box>
                </Item>
              </List>
            </Box>
          )}
        </Box>
      </Content>
    </Dropdown>
  );
}

CascaderSelect.defaultProps = {
  allowClear: false,
};

export default CascaderSelect;
